import React, { useCallback, useEffect, useState } from 'react';
import {
  SystemAdminDataService,
  SystemAdminOrgSubscription,
  SubscriptionPeriod,
  PricingTierVersion,
  UpdateEntitlementPayload,
  SUBSCRIPTION_STATUS_LABELS,
  capacityState,
  formatPrice,
} from '../../services/systemAdminDataService';
import SubscriptionEditModal from './SubscriptionEditModal';

interface SubscriptionPanelProps {
  /** The organisation whose commercial state is shown. */
  orgId: number;
  /** Named in the change-subscription confirmation. */
  orgName: string;
}

/** A dash rather than an empty cell — "not set" should look deliberate. */
const NONE = <span className="text-muted">—</span>;

const fmtDate = (iso: string | null): React.ReactNode => {
  if (!iso) return NONE;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Label + value row. Values stay left-aligned so they scan as a column. */
const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div className="sys-field">
    <div className="sys-field__label">
      {label}
      {hint && <i className="fas fa-circle-info" title={hint} aria-hidden="true" />}
    </div>
    <div className="sys-field__value">{children}</div>
  </div>
);

const Card: React.FC<{
  title: string;
  icon: string;
  children: React.ReactNode;
  /** Optional control in the card's header row, e.g. its edit affordance. */
  action?: React.ReactNode;
  /**
   * How this card commits. Stated per card because the panel mixes two
   * patterns on purpose — radios apply on click, typed values need a Save —
   * and a control that saves without saying so is worse than either alone.
   */
  hint?: string;
}> = ({ title, icon, children, action, hint }) => (
  <section className="ivory-panel sys-panel sys-panel--pad">
    <div className="sys-panel__head">
      <h2 className="sys-panel__title">
        <i className={`fas ${icon}`} aria-hidden="true" />{title}
      </h2>
      {action ?? (hint && <span className="sys-panel__hint">{hint}</span>)}
    </div>
    {children}
  </section>
);

/**
 * Acknowledges a save beside the field that saved. The radios below commit on
 * click, so without this there is nothing to tell "saved" apart from "ignored
 * my click" — and an admin who can't tell will click again.
 */
const Saved: React.FC<{ show: boolean }> = ({ show }) =>
  show ? (
    <span className="sys-saved" role="status">
      <i className="fas fa-check" aria-hidden="true" />Saved
    </span>
  ) : null;

/**
 * A small set of mutually exclusive states. Radios rather than a toggle
 * because several of these have THREE states, and the third ("inherit") is the
 * one a toggle cannot express.
 *
 * Commits on click — a radio IS the decision, so a separate Save would only
 * add a step. The card says so, and <Saved> confirms it happened.
 */
const Choice: React.FC<{
  name: string;
  value: string;
  options: { value: string; label: string; hint?: string }[];
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({ name, value, options, disabled, onChange }) => (
  <div className="sys-choice" role="radiogroup" aria-label={name}>
    {options.map(o => (
      <label key={o.value} className={`sys-choice__opt${value === o.value ? ' is-on' : ''}`}>
        <input
          type="radio"
          name={`sys-${name}`}
          checked={value === o.value}
          disabled={disabled}
          onChange={() => onChange(o.value)}
        />
        <span className="sys-choice__label">{o.label}</span>
        {o.hint && <span className="sys-choice__hint">{o.hint}</span>}
      </label>
    ))}
  </div>
);

/**
 * Grant extra headroom, or hand the organisation back to its tier's allowance.
 *
 * Typed values commit explicitly — saving per keystroke would write nonsense on
 * the way to a number. The Save button is always rendered and merely disabled
 * until something changes: a control that only appears once you have edited is
 * a control you cannot find before you edit.
 */
const SeatOverrideControl: React.FC<{
  seatOverride: number | null;
  tierMax: number | null;
  disabled?: boolean;
  saved?: boolean;
  onChange: (value: number | null) => void;
}> = ({ seatOverride, tierMax, disabled, saved, onChange }) => {
  const [draft, setDraft] = useState<string>(seatOverride?.toString() ?? '');
  useEffect(() => { setDraft(seatOverride?.toString() ?? ''); }, [seatOverride]);

  const parsed = parseInt(draft, 10);
  const valid = Number.isInteger(parsed) && parsed > 0;
  const dirty = (seatOverride?.toString() ?? '') !== draft.trim();
  const canSave = dirty && valid && !disabled;

  return (
    <span className="sys-inline-edit">
      <input
        type="number"
        min={1}
        className="form-control form-control-sm"
        style={{ width: '6.5rem' }}
        value={draft}
        placeholder={tierMax != null ? `${tierMax}` : 'unlimited'}
        disabled={disabled}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && canSave) onChange(parsed); }}
        aria-label="Children permitted for this organisation"
      />
      <span className="sys-field__note">
        {seatOverride == null
          ? `using the tier’s ${tierMax ?? 'unlimited'}`
          : `instead of the tier’s ${tierMax ?? 'unlimited'}`}
      </span>
      <button type="button" className="sys-back" disabled={!canSave} onClick={() => onChange(parsed)}>
        Save
      </button>
      {seatOverride != null && (
        <button type="button" className="sys-back" disabled={disabled} onClick={() => onChange(null)}>
          Use the tier&rsquo;s
        </button>
      )}
      <Saved show={!!saved} />
    </span>
  );
};

/** The founding-provisions rate and anything like it. Per org, never a price. */
const DiscountControl: React.FC<{
  percent: number;
  endsAt: string | null;
  disabled?: boolean;
  saved?: boolean;
  onChange: (percent: number, endsAt: string | null) => void;
}> = ({ percent, endsAt, disabled, saved, onChange }) => {
  const [pct, setPct] = useState(percent.toString());
  const [until, setUntil] = useState(endsAt ?? '');
  useEffect(() => { setPct(percent.toString()); setUntil(endsAt ?? ''); }, [percent, endsAt]);

  const parsed = parseFloat(pct);
  const valid = Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;
  const dirty = parsed !== percent || (until || null) !== endsAt;
  const canSave = dirty && valid && !disabled;
  const commit = () => onChange(parsed, until || null);

  return (
    <span className="sys-inline-edit">
      <input
        type="number" min={0} max={100} step={1}
        className="form-control form-control-sm"
        style={{ width: '5.5rem' }}
        value={pct}
        disabled={disabled}
        onChange={e => setPct(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && canSave) commit(); }}
        aria-label="Discount percentage"
      />
      <span className="sys-field__note">% until</span>
      <input
        type="date"
        className="form-control form-control-sm"
        style={{ width: '10rem' }}
        value={until}
        disabled={disabled}
        onChange={e => setUntil(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && canSave) commit(); }}
        aria-label="Discount end date"
      />
      <button type="button" className="sys-back" disabled={!canSave} onClick={commit}>
        Save
      </button>
      <Saved show={!!saved} />
    </span>
  );
};

/** Why this organisation is set up the way it is — read a year from now. */
const NotesControl: React.FC<{
  value: string | null;
  disabled?: boolean;
  saved?: boolean;
  onSave: (value: string) => void;
}> = ({ value, disabled, saved, onSave }) => {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  const dirty = draft !== (value ?? '');

  return (
    <>
      <textarea
        className="form-control"
        rows={2}
        value={draft}
        disabled={disabled}
        placeholder="Why this organisation is set up the way it is"
        onChange={e => setDraft(e.target.value)}
        aria-label="Subscription notes"
      />
      <div className="mt-2 d-flex gap-2 align-items-center">
        <button type="button" className="sys-back" disabled={!dirty || disabled} onClick={() => onSave(draft)}>
          Save notes
        </button>
        <button type="button" className="sys-back" disabled={!dirty || disabled} onClick={() => setDraft(value ?? '')}>
          Discard
        </button>
        <Saved show={!!saved} />
      </div>
    </>
  );
};

/**
 * System Admin → Organisations → one organisation → Subscription.
 *
 * READ-ONLY (Phase 2). Shows the tier an organisation is on, what that tier
 * permits, how many children they actually have, and the AI entitlement in
 * force — including whether it comes from the tier or from a per-org override.
 * Editing arrives in Phase 3; until then tier changes are a DB edit.
 */
const SubscriptionPanel: React.FC<SubscriptionPanelProps> = ({ orgId, orgName }) => {
  const [row, setRow] = useState<SystemAdminOrgSubscription | null>(null);
  const [history, setHistory] = useState<SubscriptionPeriod[]>([]);
  const [tiers, setTiers] = useState<PricingTierVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  // Set while an entitlement control is in flight, and cleared on the response.
  // Failures surface here rather than in a toast: the control that failed is on
  // screen, so the message belongs beside it.
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Which field last saved. Radios commit on click, so without an acknowledgement
  // there is nothing to distinguish "saved" from "ignored my click".
  const [savedField, setSavedField] = useState<string | null>(null);

  // One cross-org call rather than a per-org endpoint: the list is small, and a
  // second endpoint returning the same shape is a second thing to keep in step.
  // Revisit if the estate grows past a few hundred organisations.
  const load = useCallback(async () => {
    setError(null);
    try {
      const [rows, periods, book] = await Promise.all([
        SystemAdminDataService.fetchOrgSubscriptions(),
        SystemAdminDataService.fetchSubscriptionHistory(orgId),
        SystemAdminDataService.fetchPricing(),
      ]);
      setRow(rows.find(r => r.id === orgId) ?? null);
      setHistory(periods);
      setTiers(book);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load the subscription.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  // Every entitlement control goes through here: send one sparse patch, then
  // refetch so what's on screen is what the database actually holds rather than
  // an optimistic guess that can drift from it.
  const patch = async (payload: UpdateEntitlementPayload, field: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      await SystemAdminDataService.updateEntitlement(orgId, payload);
      await load();
      setSavedField(field);
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || 'That change could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  // Clear the acknowledgement after a beat. Long enough to be read, short
  // enough that a stale "Saved" never sits next to a field you have since
  // edited again.
  useEffect(() => {
    if (!savedField) return;
    const t = window.setTimeout(() => setSavedField(null), 2500);
    return () => window.clearTimeout(t);
  }, [savedField]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (error) return <div className="alert alert-danger m-3">{error}</div>;

  if (!row) {
    return <div className="text-muted text-center py-5">This organisation is no longer listed.</div>;
  }

  const sub = row.subscription;
  const counts = row.childCounts;

  // No open period at all. Possible only for an org created outside migration
  // 047's backfill — say so plainly rather than rendering an empty panel.
  if (!sub) {
    return (
      <div className="sys-page__body" style={{ paddingTop: '1.25rem' }}>
        <div className="sys-cards">
          <div className="alert alert-warning mb-0">
            <strong>No open subscription period.</strong> This organisation has no
            billing record, so it inherits nothing — full AI is off and no child
            limit applies. Every organisation should have exactly one open period.
          </div>
          <Card title="Children" icon="fa-child">
            <Field label="On roll">{counts.onRoll}</Field>
            <Field label="Leavers">{counts.leavers}</Field>
          </Card>
        </div>
      </div>
    );
  }

  const cap = capacityState(counts.onRoll, sub.maxChildren);
  // Reuses the app's existing amber (nag) and clay (urgent) families via
  // sys-state. This area's lime is chrome-only and never enters a value.
  const capState = cap === 'over' ? 'sys-state--over' : cap === 'near' ? 'sys-state--warn' : 'sys-state--on';
  const discounted = sub.discountPercent > 0 && sub.chargedPrice != null && sub.listPrice != null;

  // What the tier alone would grant — the "Inherit" option has to name the
  // outcome it would produce, or choosing it is a guess.
  const tierGrantsAi = tiers.some(t => t.code === sub.tierCode && t.isCurrent && t.aiInsightsIncluded);

  return (
    // Scroller outside, grid inside — see .sys-cards in systemAdmin.scss.
    <div className="sys-page__body" style={{ paddingTop: '1.25rem' }}>
    <div className="sys-cards">

      {saveError && (
        <div className="alert alert-danger mb-0" role="alert">{saveError}</div>
      )}

      <Card
        title="Current period"
        icon="fa-file-invoice"
        action={
          <button type="button" className="sys-back" onClick={() => setEditing(true)} disabled={saving}>
            <i className="fas fa-pen" aria-hidden="true" />Change subscription
          </button>
        }
      >
        <Field label="Tier">
          <span className="fw-semibold">{sub.tierName || sub.tierCode}</span>
          <span className="sys-field__note">{sub.tierCode}</span>
        </Field>
        <Field label="Status">{SUBSCRIPTION_STATUS_LABELS[sub.status] ?? sub.status}</Field>
        <Field label="Billing">{sub.billingPeriod === 'annual' ? 'Annual' : 'Monthly'}</Field>
        <Field label="Price">
          {/* list → charged, with the discount named. A bare number invites
              "why is that different from the website?". */}
          {discounted ? (
            <>
              <span className="text-muted text-decoration-line-through">
                {formatPrice(sub.listPrice, sub.currency)}
              </span>
              <span className="fw-semibold ms-2">{formatPrice(sub.chargedPrice, sub.currency)}</span>
              <span className="sys-field__note">
                {sub.discountPercent}% discount
                {sub.discountEndsAt && <> until {fmtDate(sub.discountEndsAt)}</>}
              </span>
            </>
          ) : (
            <span className="fw-semibold">{formatPrice(sub.listPrice, sub.currency)}</span>
          )}
          <span className="sys-field__note">
            {sub.listPrice == null ? '' : sub.billingPeriod === 'annual' ? '/yr' : '/mo'}
          </span>
        </Field>
        <Field label="Started">{fmtDate(sub.startedAt)}</Field>
        <Field label="Trial ends">{fmtDate(sub.trialEndsAt)}</Field>
        <Field label="Renews">{fmtDate(sub.renewsAt)}</Field>
      </Card>

      <Card title="Capacity" icon="fa-child" hint="Choices apply as you make them">
        <Field
          label="Children on roll"
          hint="Every child not marked as left or deleted — inactive children count too. Leavers are retained for compliance and not billed."
        >
          <span className={`sys-state ${capState}`} style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {cap === 'over' && <i className="fas fa-triangle-exclamation" aria-hidden="true" />}
            {counts.onRoll}
          </span>
          <span className="sys-field__note">of {sub.maxChildren ?? 'unlimited'}</span>
          {cap === 'over' && sub.maxChildren != null && (
            <span className="sys-state sys-state--over ms-2">
              {counts.onRoll - sub.maxChildren} over
            </span>
          )}
        </Field>
        <Field label="Leavers" hint="Flagged as left, retained for compliance, never billed.">
          {counts.leavers > 0 ? counts.leavers : NONE}
        </Field>
        <Field
          label="Seat override"
          hint="Extra headroom granted without moving the organisation up a tier."
        >
          <SeatOverrideControl
            seatOverride={sub.seatOverride}
            tierMax={sub.tierMaxChildren}
            disabled={saving}
            saved={savedField === 'seats'}
            onChange={v => patch({ seatOverride: v }, 'seats')}
          />
        </Field>

        {/* Consequences, not adjectives — flipping this stops staff adding a
            child, so the option has to say so before it is chosen. */}
        <Field label="Cap enforcement">
          <Choice
            name="cap"
            disabled={saving}
            value={sub.enforceChildCap ? 'hard' : 'soft'}
            onChange={v => patch({ enforceChildCap: v === 'hard' }, 'cap')}
            options={[
              { value: 'soft', label: 'Soft', hint: 'going over emails us; nothing is blocked' },
              { value: 'hard', label: 'Hard', hint: 'staff cannot add a child beyond the limit' },
            ]}
          />
          <Saved show={savedField === 'cap'} />
        </Field>
      </Card>

      <Card title="Entitlements" icon="fa-wand-magic-sparkles" hint="Choices apply as you make them">
        {/* Three states, not a checkbox. "Inherit" is the one that matters:
            without it a later tier upgrade silently fails to grant the feature,
            because a forced value would keep winning. */}
        <Field label="Full AI &amp; Insights">
          <Choice
            name="ai"
            disabled={saving}
            value={!sub.aiFullIsOverride ? 'inherit' : sub.aiFullEnabled ? 'on' : 'off'}
            onChange={v => patch({ aiFullOverride: v === 'inherit' ? 'inherit' : v === 'on' }, 'ai')}
            options={[
              {
                value: 'inherit',
                label: 'Inherit from tier',
                hint: `${sub.tierName || sub.tierCode} → ${tierGrantsAi ? 'included' : 'not included'}`,
              },
              { value: 'on',  label: 'Force on',  hint: 'overrides the tier' },
              { value: 'off', label: 'Force off', hint: 'overrides the tier' },
            ]}
          />
          <Saved show={savedField === 'ai'} />
        </Field>
        <Field label="In force now">
          {sub.aiFullEnabled ? (
            <span className="sys-state sys-state--on" style={{ fontWeight: 600 }}>
              <i className="fas fa-check-circle" aria-hidden="true" />Full AI and Insights
            </span>
          ) : (
            <span className="sys-state sys-state--off">
              <i className="fas fa-minus-circle" aria-hidden="true" />Rewrite only
            </span>
          )}
          <span className="sys-field__note">
            {sub.aiFullIsOverride
              ? 'set here, not inherited'
              : `inherited from ${sub.tierName || sub.tierCode}`}
          </span>
        </Field>
      </Card>

      <Card title="Discount" icon="fa-tag" hint="Save to apply">
        <Field label="Rate" hint="A per-organisation concession, not a change to the published price.">
          <DiscountControl
            percent={sub.discountPercent}
            endsAt={sub.discountEndsAt}
            disabled={saving}
            saved={savedField === 'discount'}
            onChange={(percent, endsAt) =>
              patch({ discountPercent: percent, discountEndsAt: endsAt }, 'discount')}
          />
        </Field>
      </Card>

      <Card title="Notes" icon="fa-note-sticky" hint="Save to apply">
        <NotesControl
          value={sub.notes}
          disabled={saving}
          saved={savedField === 'notes'}
          onSave={v => patch({ notes: v }, 'notes')}
        />
      </Card>

      {history.length > 1 && (
        <Card title="History" icon="fa-clock-rotate-left">
          <div className="sys-table-scroll">
            <table className="sys-table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Status</th>
                  <th>From</th>
                  <th>To</th>
                  <th className="sys-table__num">Price then</th>
                </tr>
              </thead>
              <tbody>
                {history.map(p => (
                  <tr key={p.id}>
                    <td className="sys-table__name">{p.tierName || p.tierCode}</td>
                    <td className="sys-table__muted">{SUBSCRIPTION_STATUS_LABELS[p.status] ?? p.status}</td>
                    <td className="sys-table__muted">{fmtDate(p.startedAt)}</td>
                    <td className="sys-table__muted">
                      {p.isOpen ? <span className="sys-state sys-state--on">Current</span> : fmtDate(p.endedAt)}
                    </td>
                    {/* Priced against the book version in force when the period
                        started — the reason the price book is dated at all. */}
                    <td className="sys-table__num">{formatPrice(p.priceThen, p.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>

    {editing && (
      <SubscriptionEditModal
        orgId={orgId}
        orgName={orgName}
        current={sub}
        tiers={tiers}
        onClose={() => setEditing(false)}
        onSaved={load}
      />
    )}
    </div>
  );
};

export default SubscriptionPanel;
